<template>
    <div class="block-editor cache-write">
        <div class="wrapper">
            <!-- Cache Key -->
            <section>
                <div class="title">{{ $t("Cache Key") }}</div>
                <TiptapEditor
                    :placeholder="$t('Örnek: user_{{contact._id}}_session')"
                    :show-attributes="true"
                    v-model="clone.key"
                    automation-mode
                />
                <div class="help-text">
                    <i class="material-icons">info</i>
                    <span>Key unique olmalıdır. Aynı key ile tekrar yazarsanız değer güncellenir.</span>
                </div>
            </section>

            <!-- Cache Value -->
            <section>
                <div class="title">{{ $t("Cache Value") }}</div>
                <TiptapEditor
                    :placeholder="$t('Kaydedilecek değer')"
                    :show-attributes="true"
                    v-model="clone.value"
                    automation-mode
                />
            </section>

            <!-- Cache Type -->
            <section>
                <div class="title">{{ $t("Cache Tipi") }}</div>
                <CustomPicker
                    v-model="clone.type"
                    :items="typeOptions"
                    idKey="value"
                    labelKey="label"
                    :placeholder="$t('Cache tipini seçin')"
                />
                <div class="help-text">
                    <i class="material-icons">info</i>
                    <span v-if="clone.type === 'public'">Public cache'ler tüm modüller tarafından okunabilir ve güncellenebilir.</span>
                    <span v-else>Private cache'ler sadece oluşturan modül tarafından güncellenebilir. UI'dan manuel güncellenebilir.</span>
                </div>
            </section>

            <!-- Data Type -->
            <section>
                <div class="title">{{ $t("Data Type") }}</div>
                <CustomPicker
                    v-model="clone.dataType"
                    :items="dataTypeOptions"
                    idKey="value"
                    labelKey="label"
                    :placeholder="$t('Data tipini seçin')"
                />
                <div class="help-text">
                    <i class="material-icons">info</i>
                    <span>Data tipi sadece UI'da gösterim için kullanılır, validation yapılmaz.</span>
                </div>
            </section>

            <!-- Expires At -->
            <section>
                <div class="title">{{ $t("Geçerlilik Süresi") }}</div>
                <div class="expires-config">
                    <CustomPicker
                        v-model="clone.expiresAt.mode"
                        :items="expiresModeOptions"
                        idKey="value"
                        labelKey="label"
                        :placeholder="$t('Süre tipi')"
                    />

                    <div v-if="clone.expiresAt.mode === 'duration'" class="duration-config">
                        <TiptapEditor
                            :placeholder="$t('Süre değeri (örn: 24)')"
                            :show-attributes="true"
                            v-model="clone.expiresAt.duration.value"
                            automation-mode
                        />
                        <CustomPicker
                            v-model="clone.expiresAt.duration.unit"
                            :items="durationUnitOptions"
                            idKey="value"
                            labelKey="label"
                            :placeholder="$t('Birim')"
                        />
                    </div>

                    <div v-if="clone.expiresAt.mode === 'timestamp'" class="timestamp-config">
                        <TiptapEditor
                            :placeholder="$t('ISO tarih (örn: 2025-12-31T23:59:59.000Z)')"
                            :show-attributes="true"
                            v-model="clone.expiresAt.timestamp.value"
                            automation-mode
                        />
                    </div>
                </div>
            </section>
        </div>
    </div>
</template>

<script>
import _ from "lodash";
import TiptapEditor from "../../../../../Chatbot/pages/dashboard/common/TiptapEditor.vue";
import CustomPicker from "../../../../../../../components/pickers/CustomPicker.vue";

export default {
    name: "CacheWrite",
    components: {
        TiptapEditor,
        CustomPicker,
    },
    props: ["value"],
    data() {
        return {
            clone: _.cloneDeep(this.value || {
                key: "",
                value: "",
                type: "public",
                dataType: "mixed",
                expiresAt: {
                    mode: "never",
                    duration: {
                        value: "1",
                        unit: "hours",
                    },
                    timestamp: {
                        value: "",
                    },
                },
            }),
            typeOptions: [
                { value: "public", label: "Public (Herkes erişebilir)" },
                { value: "private", label: "Private (Sadece oluşturan modül)" },
            ],
            dataTypeOptions: [
                { value: "mixed", label: "Mixed (Karışık)" },
                { value: "string", label: "String (Metin)" },
                { value: "number", label: "Number (Sayı)" },
                { value: "boolean", label: "Boolean (Evet/Hayır)" },
                { value: "date", label: "Date (Tarih)" },
                { value: "json", label: "JSON (Object)" },
                { value: "array", label: "Array (Dizi)" },
                { value: "objectid_user", label: "User ID" },
            ],
            expiresModeOptions: [
                { value: "never", label: "Hiçbir zaman expire olmasın" },
                { value: "duration", label: "Süre belirt (örn: 24 saat)" },
                { value: "timestamp", label: "Tarih belirt" },
            ],
            durationUnitOptions: [
                { value: "seconds", label: "Saniye" },
                { value: "minutes", label: "Dakika" },
                { value: "hours", label: "Saat" },
                { value: "days", label: "Gün" },
            ],
        };
    },
    watch: {
        clone: {
            deep: true,
            handler(newValue) {
                this.$emit("input", newValue);
            },
        },
    },
};
</script>

<style lang="scss">
.block-editor.cache-write {
    .wrapper {
        padding: 30px;

        section {
            margin-bottom: 20px;

            .title {
                margin-bottom: 10px;
                font-weight: 600;
                font-size: 16px;
                line-height: 20px;
            }

            .help-text {
                display: flex;
                align-items: flex-start;
                gap: 8px;
                margin-top: 8px;
                padding: 10px;
                background: #f0f8ff;
                border-radius: 6px;
                font-size: 13px;
                color: #666;

                .material-icons {
                    font-size: 18px;
                    color: #1890ff;
                }
            }

            .expires-config {
                display: flex;
                flex-direction: column;
                gap: 15px;

                .duration-config {
                    display: flex;
                    gap: 10px;
                    align-items: flex-start;

                    > :first-child {
                        flex: 2;
                    }

                    > :last-child {
                        flex: 1;
                    }
                }

                .timestamp-config {
                    display: flex;
                    flex-direction: column;
                }
            }
        }
    }
}
</style>


